import { Eye, EyeOff, MapPinned } from "lucide-react";
import { useState } from "react";

import { ChoiceCard, Disclosure, DisclosureChevron, PanelGroup, PanelRow } from "../components/panel";
import { Motion, Presence } from "../components/presence";
import { SaveDossierHint } from "../components/SaveDossierHint";
import { useMediaQuery } from "../components/useMediaQuery";
import { FloatingPanel, IconButton, SearchField } from "../components/ui";
import { HelpButton } from "../help/HelpButton";
import { t } from "../i18n/i18n";
import { SymbolThumb } from "../map/annotations/AnnotationMenu";
import {
  type AnnotationCategoryKey,
  type AnnotationSymbol,
  CATEGORIES,
  identityName,
  labelled,
  sidcOf,
  symbolOf,
  symbolsOf,
} from "../map/annotations/catalog";
import { chiefChanges, chiefTitle } from "../map/annotations/chiefNotice";
import { inherited, nameOf, positionAt, useAnnotations } from "../map/annotations/useAnnotations";
import { useDeployment } from "../map/units/useDeployment";
import { annotationControl, useUi } from "../state/ui";
import { useLeavingList } from "./leavingList";

/** Annotations APP-6 (« Objets de la mission ») : palette des 71 symboles en cinq catégories repliables, avec
 *  l'aperçu de chaque symbole et une recherche ; le choix d'un symbole le porte sous le pointeur, un clic le pose
 *  (Échap annule) ; puis la liste des annotations du dossier, qui ouvre leur fiche. */

/** Symboles d'une catégorie qui répondent à la recherche (nom FR ou EN, sigle), sans tenir compte des accents. */
export function matching(symbols: readonly AnnotationSymbol[], query: string): AnnotationSymbol[] {
  const fold = (text: string) => text.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();
  const wanted = fold(query.trim());
  if (!wanted) return [...symbols];
  return symbols.filter((s) => [s.fr, s.en, s.short ?? ""].some((text) => fold(text).includes(wanted)));
}

export function AnnotationsPanel() {
  const { items, canEdit, guard } = useAnnotations();
  const { data: deployment } = useDeployment();
  const chief = deployment?.chief_annotations ?? null;
  const fromChief = items.filter(inherited).length;
  const draft = useUi((s) => s.draft);
  const setDraft = useUi((s) => s.setDraft);
  const select = useUi((s) => s.select);
  const selection = useUi((s) => s.selection);
  const requestFlyTo = useUi((s) => s.requestFlyTo);
  const phase = useUi((s) => s.phase);
  const controls = useUi((s) => s.controlVisible);
  const setControlVisible = useUi((s) => s.setControlVisible);
  const coarse = useMediaQuery("(pointer: coarse)");
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState<Partial<Record<AnnotationCategoryKey, boolean>>>({ units: true });
  const entries = useLeavingList(items, (item) => item.id);
  const selectedId = selection?.layer === "annotation" ? selection.id : null;
  const allVisible = CATEGORIES.every((c) => controls[annotationControl(c.key)] !== false);

  /** Choix d'un symbole : la pose commence (ou s'arrête, second clic sur le même). */
  const start = (symbol: AnnotationSymbol) => {
    if (draft?.family === "annotation" && draft.kind === symbol.key) {
      setDraft(null);
      return;
    }
    if (!guard(t("poser des annotations"))) return;
    const control = annotationControl(symbol.category);
    if (controls[control] === false) setControlVisible(control, true);
    select(null);
    setDraft({ family: "annotation", kind: symbol.key, name: labelled(symbol), color: "", sidc: sidcOf(symbol.key), points: [], hover: null });
  };

  return (
    <FloatingPanel
      enter="left"
      width="view"
      icon={MapPinned}
      title={t("Annotations")}
      actions={
        <>
          <IconButton
            size="sm"
            onClick={() => CATEGORIES.forEach((c) => setControlVisible(annotationControl(c.key), !allVisible))}
            title={allVisible ? t("Masquer les annotations sur la carte") : t("Afficher les annotations sur la carte")}
            aria-label={allVisible ? t("Masquer les annotations sur la carte") : t("Afficher les annotations sur la carte")}
            aria-pressed={!allVisible}
          >
            {allVisible ? <Eye size={16} /> : <EyeOff size={16} />}
          </IconButton>
          <HelpButton screen="carte" topic="carte.annotations" buttonSize="sm" size={16} />
        </>
      }
    >
      <Presence show={!canEdit}>
        <Motion origin="fade">
          <SaveDossierHint action="poser des annotations" variant="banner" />
        </Motion>
      </Presence>
      <div className="px-4 pt-3 pb-1">
        <SearchField value={query} onChange={setQuery} placeholder={t("Rechercher un symbole")} size="sm" />
      </div>
      {CATEGORIES.map((category) => {
        const symbols = matching(symbolsOf(category.key), query);
        const expanded = query.trim() ? symbols.length > 0 : open[category.key] === true;
        const id = `annotations-${category.key}`;
        return (
          <PanelGroup
            key={category.key}
            flush
            title={
              <button
                type="button"
                className="flex w-full items-center gap-1.5 text-left"
                aria-expanded={expanded}
                aria-controls={id}
                onClick={() => setOpen((o) => ({ ...o, [category.key]: !expanded }))}
              >
                <DisclosureChevron open={expanded} />
                {labelled(category)}
              </button>
            }
            count={symbols.length}
          >
            <Disclosure open={expanded} id={id}>
              <div className="grid grid-cols-1 gap-1.5 px-4 pt-1 pb-2">
                {symbols.map((symbol) => (
                  <ChoiceCard
                    key={symbol.key}
                    selected={draft?.family === "annotation" && draft.kind === symbol.key}
                    onClick={() => start(symbol)}
                    icon={<SymbolThumb sidc={sidcOf(symbol.key)} />}
                    title={labelled(symbol)}
                    data-annotation-symbol={symbol.key}
                  />
                ))}
              </div>
            </Disclosure>
          </PanelGroup>
        );
      })}
      {canEdit && (
        <p className="px-4 pt-2 pb-1 text-meta leading-snug text-ink-muted">
          {coarse
            ? t("Choisissez un symbole puis touchez la carte à l'endroit voulu.")
            : t("Choisissez un symbole puis cliquez sur la carte. Clic droit sur la carte : « Ajouter une annotation ici ».")}
        </p>
      )}

      <PanelGroup title={t("Dans le dossier")} count={items.length}>
        <Presence show={Boolean(chief) || fromChief > 0}>
          <Motion origin="fade">
            <p className="px-2 pb-2 text-meta leading-snug text-ink-muted" data-annotation-chief>
              {chief
                ? `${chiefChanges(chief) ? t("{title} : {changes}.", { title: chiefTitle(chief), changes: chiefChanges(chief) }) : `${chiefTitle(chief)}.`} `
                : ""}
              {t("Les annotations de l'échelon supérieur sont en lecture seule.")}
            </p>
          </Motion>
        </Presence>
        {entries.length === 0 ? (
          <p className="px-2 pb-1 text-ui leading-snug text-ink-muted">
            {t("Aucune annotation. Choisissez un symbole ci-dessus, puis posez-le sur la carte.")}
          </p>
        ) : (
          <ul className="space-y-px pb-1">
            {entries.map(({ key, item, present }) => {
              const symbol = symbolOf(item.key);
              const at = positionAt(item, phase);
              return (
                <Presence key={key} show={present}>
                  <Motion origin="fade" role="listitem">
                    <PanelRow
                      icon={<SymbolThumb square sidc={item.sidc ?? sidcOf(item.key, item.identity)} />}
                      title={nameOf(item)}
                      detail={`${symbol ? labelled(symbol) : item.key} · ${identityName(item.identity)}${inherited(item) ? ` · ${t("Échelon supérieur")}` : ""}`}
                      selected={selectedId === item.id}
                      onClick={() => {
                        select({ layer: "annotation", id: item.id, lngLat: at });
                        requestFlyTo(at, 13);
                      }}
                    />
                  </Motion>
                </Presence>
              );
            })}
          </ul>
        )}
      </PanelGroup>
    </FloatingPanel>
  );
}
